import React, { Component, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import HomePage from "./pages/HomePage";
import { Toaster } from "./components/ui/sonner";

// The homepage ships in the main bundle so the landing page renders immediately.
// Other pages are code-split: their code (and the university list) downloads only
// when a visitor opens them.

const RELOAD_FLAG = "xa-chunk-reload";

const readFlag = () => {
  try { return sessionStorage.getItem(RELOAD_FLAG); } catch { return "1"; }
};
const writeFlag = (value) => {
  try {
    if (value) sessionStorage.setItem(RELOAD_FLAG, value);
    else sessionStorage.removeItem(RELOAD_FLAG);
  } catch { /* storage unavailable (e.g. private mode) */ }
};

// After a new deploy, Netlify no longer serves the old page files. A visitor who had the
// site open would then fail to load the next page, so reload once to pick up the new version.
const lazyWithRetry = (importPage) =>
  lazy(() =>
    importPage()
      .then((module) => {
        writeFlag(null);
        return module;
      })
      .catch((error) => {
        if (!readFlag()) {
          writeFlag("1");
          window.location.reload();
          return new Promise(() => {}); // wait for the reload
        }
        throw error;
      })
  );

const UniversitiesPage = lazyWithRetry(() => import("./pages/UniversitiesPage"));
const NursingInGermanyPage = lazyWithRetry(() => import("./pages/NursingInGermanyPage"));
const WorkforceManagementPage = lazyWithRetry(() => import("./pages/WorkforceManagementPage"));
const MBBSAbroadPage = lazyWithRetry(() => import("./pages/MBBSAbroadPage"));
const PhDAdmissionsPage = lazyWithRetry(() => import("./pages/PhDAdmissionsPage"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white" role="status" aria-label="Loading page">
    <div className="w-10 h-10 rounded-full border-4 border-navy-100 border-t-brand-500 animate-spin" />
  </div>
);

// If a page still can't load (e.g. the visitor went offline), show a way out instead of a blank screen.
class PageErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-white">
        <h1 className="font-display text-2xl font-bold text-navy-900">This page could not load</h1>
        <p className="text-slate-500">Please check your connection and try again.</p>
        <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
          Reload page
        </button>
      </div>
    );
  }
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PageErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/universities" element={<UniversitiesPage />} />
              <Route path="/nursing-in-germany" element={<NursingInGermanyPage />} />
              <Route path="/workforce-management" element={<WorkforceManagementPage />} />
              <Route path="/mbbs-abroad" element={<MBBSAbroadPage />} />
              <Route path="/phd-admissions" element={<PhDAdmissionsPage />} />
            </Routes>
          </Suspense>
        </PageErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
